import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import api from 'api/client';

const borderColor = 'rgba(245, 158, 11, 0.35)';
const cardSx = { border: '1px solid', borderColor, borderRadius: 3, boxShadow: 'none' };

const stateStyles = {
  healthy: { bg: 'rgba(22,163,74,0.12)', color: '#166534' },
  warning: { bg: 'rgba(245,158,11,0.14)', color: '#92400e' },
  unhealthy: { bg: 'rgba(220,38,38,0.12)', color: '#991b1b' },
  disabled: { bg: 'rgba(100,116,139,0.15)', color: '#334155' }
};

const workerStateStyles = {
  healthy: { bg: 'rgba(22,163,74,0.12)', color: '#16a34a', border: 'rgba(22,163,74,0.4)', dot: '#16a34a', label: 'Operational' },
  degraded: { bg: 'rgba(245,158,11,0.14)', color: '#d97706', border: 'rgba(245,158,11,0.45)', dot: '#f59e0b', label: 'Degraded' },
  down: { bg: 'rgba(220,38,38,0.12)', color: '#dc2626', border: 'rgba(220,38,38,0.45)', dot: '#ef4444', label: 'Down' }
};

const fmtAge = (seconds) => {
  const s = Number(seconds || 0);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const percentColor = (value) => {
  const pct = Number(value || 0);
  if (pct >= 90) return '#dc2626';
  if (pct >= 75) return '#f59e0b';
  return '#16a34a';
};

const AdminSystemHealthPage = () => {
  const [payload, setPayload] = useState(null);
  const [smokePayload, setSmokePayload] = useState(null);
  const [smokeLoading, setSmokeLoading] = useState(false);
  const [smokeError, setSmokeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/admin/system/health/');
      setPayload(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load health status.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Live-refresh worker/queue health every 10s so admins see the queue draining.
    const interval = setInterval(() => load(true), 10000);
    return () => clearInterval(interval);
  }, [load]);

  const runSmoke = useCallback(async () => {
    setSmokeLoading(true);
    setSmokeError('');
    try {
      const { data } = await api.get('/api/admin/system/smoke/');
      setSmokePayload(data || null);
    } catch (err) {
      setSmokeError(err?.response?.data?.detail || err?.message || 'Failed to run smoke checks.');
    } finally {
      setSmokeLoading(false);
    }
  }, []);

  const checks = payload?.checks || [];
  const summary = payload?.summary || {};
  const kpis = payload?.kpis || {};
  const resources = payload?.resources || {};
  const worker = payload?.worker || null;
  const workerUi = workerStateStyles[worker?.status] || workerStateStyles.down;
  const backlog = worker?.backlog || {};

  const resourceCards = [
    {
      label: 'CPU',
      value: resources.cpu?.percent !== null && resources.cpu?.percent !== undefined ? `${resources.cpu.percent}%` : 'N/A',
      detail: `${resources.cpu?.count || 0} cores · load ${(resources.cpu?.load_average || []).slice(0, 3).map((v) => Number(v).toFixed(2)).join(' / ') || 'N/A'}`,
      pct: Number(resources.cpu?.percent || 0)
    },
    {
      label: 'Memory',
      value: resources.memory ? `${resources.memory.percent}%` : 'N/A',
      detail: resources.memory ? `${formatBytes(resources.memory.used)} / ${formatBytes(resources.memory.total)}` : 'psutil unavailable',
      pct: Number(resources.memory?.percent || 0)
    },
    {
      label: 'Disk',
      value: resources.disk ? `${resources.disk.percent}%` : 'N/A',
      detail: resources.disk ? `${formatBytes(resources.disk.used)} / ${formatBytes(resources.disk.total)}` : 'No disk snapshot',
      pct: Number(resources.disk?.percent || 0)
    },
    {
      label: 'Process',
      value: resources.process?.rss ? formatBytes(resources.process.rss) : 'N/A',
      detail: `${resources.process?.threads || 0} threads · ${resources.process?.connections || 0} connections`,
      pct: Math.min(100, Number(resources.process?.rss || 0) / 1024 / 1024)
    }
  ];

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <MonitorHeartOutlinedIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  System Health
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Real-time operational checks for tools, workers, and platform KPIs.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<ScienceIcon />}
                onClick={runSmoke}
                disabled={smokeLoading}
                sx={{
                  bgcolor: 'var(--fz-surface-1, #111827)',
                  color: '#f59e0b',
                  border: '1px solid #f59e0b',
                  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
                }}
              >
                {smokeLoading ? 'Running Smoke...' : 'Run Smoke'}
              </Button>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid', borderColor }}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {/* ── Scan Worker Health ─────────────────────────────────────────── */}
      {worker && (
        <Card sx={{ ...cardSx, borderColor: workerUi.border, borderWidth: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 14, height: 14, borderRadius: '50%', bgcolor: workerUi.dot,
                    boxShadow: `0 0 0 4px ${workerUi.bg}`,
                    animation: worker.status === 'healthy' ? 'pulseDot 1.8s ease-in-out infinite' : 'none',
                    '@keyframes pulseDot': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } }
                  }}
                />
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Scan Worker</Typography>
                    <Chip
                      size="small"
                      label={workerUi.label}
                      sx={{ bgcolor: workerUi.bg, color: workerUi.color, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                    {worker.message}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                {[
                  { k: 'Workers', v: worker.workers_online || 0, c: worker.workers_online ? '#16a34a' : '#dc2626' },
                  { k: 'Active', v: worker.active || 0, c: '#f59e0b' },
                  { k: 'Queued', v: backlog.pending || 0, c: (backlog.pending || 0) > 0 ? '#f59e0b' : '#64748b' },
                  { k: 'Running', v: backlog.running || 0, c: '#0ea5e9' },
                  { k: 'Failed', v: backlog.failed || 0, c: (backlog.failed || 0) > 0 ? '#dc2626' : '#64748b' }
                ].map((b) => (
                  <Box key={b.k} sx={{ textAlign: 'center', minWidth: 64, px: 1, py: 0.5, borderRadius: 2, border: '1px solid rgba(148,163,184,0.2)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: b.c, lineHeight: 1.1 }}>{b.v}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{b.k}</Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>

            {/* details row */}
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(148,163,184,0.15)' }}>
              {worker.status === 'down' && (
                <Alert severity="error" sx={{ width: '100%', py: 0.5 }}>
                  No Celery worker is responding. Scans will stay <strong>queued</strong> until the worker container is back online.
                  Check <code>docker logs fuzzscan-celery</code>.
                </Alert>
              )}
              {(backlog.pending || 0) > 0 && backlog.oldest_pending_age_seconds > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Oldest queued scan waiting: <strong style={{ color: backlog.oldest_pending_age_seconds > 300 ? '#dc2626' : '#92400e' }}>{fmtAge(backlog.oldest_pending_age_seconds)}</strong>
                </Typography>
              )}
              {(worker.workers || []).length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Worker nodes: {worker.workers.map((w) => w.replace('celery@', '')).join(', ')}
                </Typography>
              )}
              {worker.broker_error && (
                <Typography variant="caption" sx={{ color: '#dc2626' }}>
                  Broker error: {worker.broker_error}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Healthy</Typography>
              <Typography variant="h4" sx={{ color: '#16a34a', fontWeight: 700 }}>{summary.healthy || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Unhealthy</Typography>
              <Typography variant="h4" sx={{ color: '#dc2626', fontWeight: 700 }}>{summary.unhealthy || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Disabled</Typography>
              <Typography variant="h4" sx={{ color: '#334155', fontWeight: 700 }}>{summary.disabled || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Users</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{kpis.users || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Running Scans</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{kpis.scans_running || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={cardSx}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Infrastructure Resource Snapshot
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Host-level CPU, memory, disk, and runtime process indicators for the FuzzScan backend.
              </Typography>
            </Box>
            <Chip
              size="small"
              label={`Collector: ${resources.collector || 'unknown'}`}
              sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#92400e', fontWeight: 600 }}
            />
          </Stack>

          <Grid container spacing={2}>
            {resourceCards.map((item) => {
              const color = percentColor(item.pct);
              return (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={item.label}>
                  <Card sx={{ ...cardSx, borderColor: 'rgba(34,197,94,0.28)' }}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color }}>
                            {item.value}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(100, item.pct || 0))}
                          sx={{
                            height: 8,
                            borderRadius: 10,
                            bgcolor: 'rgba(148,163,184,0.16)',
                            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 10 }
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {item.detail}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Host: {resources.host?.hostname || '-'} · Python {resources.host?.python || '-'} · PID {resources.host?.pid || '-'}
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {checks.map((check) => {
          const ui = stateStyles[check.state] || stateStyles.unhealthy;
          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={check.id}>
              <Card sx={cardSx}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {check.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={check.state}
                      sx={{ bgcolor: ui.bg, color: ui.color, textTransform: 'capitalize', fontWeight: 600 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {check.detail || '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Card sx={cardSx}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Runtime Smoke Checks
            </Typography>
            <Chip
              size="small"
              label={`Healthy ${smokePayload?.summary?.healthy || 0} · Warning ${smokePayload?.summary?.warning || 0} · Unhealthy ${
                smokePayload?.summary?.unhealthy || 0
              }`}
              sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#92400e', fontWeight: 600 }}
            />
          </Stack>
          {smokeError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {smokeError}
            </Alert>
          )}
          {smokePayload?.checks?.length ? (
            <Stack spacing={1}>
              {smokePayload.checks.map((check) => {
                const ui = stateStyles[check.state] || stateStyles.unhealthy;
                return (
                  <Stack
                    key={check.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{ border: '1px solid rgba(245,158,11,0.18)', borderRadius: 2, px: 1.25, py: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {check.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {check.detail}
                      </Typography>
                    </Box>
                    <Chip size="small" label={check.state} sx={{ bgcolor: ui.bg, color: ui.color, textTransform: 'capitalize', fontWeight: 600 }} />
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Alert severity="info">Run smoke checks to validate core modules and integrations.</Alert>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default AdminSystemHealthPage;
